import { Dispatch, Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { loadAndTrack } from "redux-request-loading";
import axios from "axios";

import { Payload } from "@neworbit/redux-helpers";

import { toast } from "@teamprevent/common/toasts";
import {
    SUBMIT_QUESTIONNAIRE, submitQuestionnaireSuccess,
    submitInvalidQuestionnaire, questionnaireLoaded, saveQuestionnaireSuccess
} from "@teamprevent/common/questionnaires/actions";
import { TaskModel } from "@teamprevent/common/task/taskActions/models";
import { AnsweredQuestionWithId } from "@teamprevent/common/questionnaires/model";
import { PpqOutcome } from "@teamprevent/common/ppq";
import { navigateToParentRoute } from "@teamprevent/common/routing/actions";

import { TasksUpdatedSuccessResult, AddTaskFormModel } from "../task/model";
import { consultationLoaded } from "../consultations/actions";
import { ApplicationState } from "../applicationState";

import { outcomeQuestionId } from "./components/PpqClinicalNotes";
import * as actions from "./actionTypes";
import { ClinicalNotesApi } from "./ClinicalNotesApi";
import { questionnaireKey as ClinicalNotesQuestionnaireKey } from "./components/ClinicalNotes";

export const SUBMIT_DID_NOT_ATTEND = "SUBMIT_DID_NOT_ATTEND";
export const SAVE_REFERRAL_CLINICAL_NOTES = "SAVE_REFERRAL_CLINICAL_NOTES";
export const SAVE_PHYSIOTHERAPY_REFERRAL_CLINICAL_NOTES = "SAVE_PHYSIOTHERAPY_REFERRAL_CLINICAL_NOTES";
export const SAVE_PPQ_CLINICAL_NOTES = "SAVE_PPQ_CLINICAL_NOTES";

export type SubmitClinicalNotesSuccess = ({ type: typeof actions.SUBMIT_CLINICAL_NOTES_SUCCESS }) & Payload<TasksUpdatedSuccessResult>;
export type SubmitDidNotAttendSuccess = ({ type: typeof actions.SUBMIT_DID_NOT_ATTEND_SUCCESS }) & Payload<TasksUpdatedSuccessResult>;

export type ClinicalNotesAction = SubmitClinicalNotesSuccess | SubmitDidNotAttendSuccess;

export const loadQuestionnaire = buildLoadQuestionnaire(new ClinicalNotesApi(axios));

export function buildLoadQuestionnaire(clinicalNotesApi: ClinicalNotesApi) {
    return (abortController: AbortController, ohCaseId: string, taskId: string, questionnaireKey: string) => {
        return async (dispatch: Dispatch) => {
            dispatch(questionnaireLoaded({ questions: [], questionnaireKey }));
            const response = await loadAndTrack(dispatch, "loadQuestionnaire", clinicalNotesApi.load(ohCaseId, taskId, abortController));
            if (response) {
                const questions = response.data.questions || [];
                dispatch(questionnaireLoaded({ questions, questionnaireKey, eTag: response.eTag }));
            }
        };
    };
}

export const buildSaveReferralClinicalNotes = (clinicalNotesApi: ClinicalNotesApi) => {
    return (ohCaseId: string, taskId: string, customerId: string, questions: AnsweredQuestionWithId[], eTag: string) => {
        return async (dispatch: ThunkDispatch<ApplicationState, undefined, Action>) => {
            const response =
                await loadAndTrack(dispatch, SAVE_REFERRAL_CLINICAL_NOTES, clinicalNotesApi.saveReferralClinicalNotes(ohCaseId, taskId, questions, eTag));
            dispatch(saveQuestionnaireSuccess({ questionnaireKey: ClinicalNotesQuestionnaireKey, eTag: response.eTag }));
        };
    };
};

export const saveReferralClinicalNotes = buildSaveReferralClinicalNotes(new ClinicalNotesApi(axios));

export const submitReferralClinicalNotes = buildSubmitReferralClinicalNotes(new ClinicalNotesApi(axios));

export function buildSubmitReferralClinicalNotes(clinicalNotesApi: ClinicalNotesApi) {
    return (ohCaseId: string, taskId: string, customerId: string, questions: AnsweredQuestionWithId[], questionnaireKey: string) => {
        if (!questions.every(question => question.isValid)) {
            return (dispatch: Dispatch) => dispatch(submitInvalidQuestionnaire(questionnaireKey));
        }
        return async (dispatch: ThunkDispatch<ApplicationState, undefined, Action>) => {
            const result = await loadAndTrack(dispatch, SUBMIT_QUESTIONNAIRE, clinicalNotesApi.submitReferralClinicalNotes(ohCaseId, taskId));
            toast.success("Clinical Notes submitted successfully");
            dispatch(submitQuestionnaireSuccess(questionnaireKey));
            dispatch(submitClinicalNotesSuccess({
                ohCaseId,
                tasks: result
            }));
            dispatch(navigateToParentRoute());
        };
    };
}

export const buildSavePhysiotherapyReferralClinicalNotes = (clinicalNotesApi: ClinicalNotesApi) => {
    return (ohCaseId: string, taskId: string, customerId: string, questions: AnsweredQuestionWithId[], eTag: string) => {
        return async (dispatch: ThunkDispatch<ApplicationState, undefined, Action>) => {
            const response =
                await loadAndTrack(
                    dispatch,
                    SAVE_PHYSIOTHERAPY_REFERRAL_CLINICAL_NOTES,
                    clinicalNotesApi.savePhysiotherapyReferralClinicalNotes(ohCaseId, taskId, questions, eTag));

            dispatch(saveQuestionnaireSuccess({ questionnaireKey: ClinicalNotesQuestionnaireKey, eTag: response.eTag }));
        };
    };
};

export function buildSubmitPhysiotherapyReferralClinicalNotes(clinicalNotesApi: ClinicalNotesApi) {
    return (ohCaseId: string, taskId: string, customerId: string, questions: AnsweredQuestionWithId[], questionnaireKey: string) => {
        if (!questions.every(question => question.isValid)) {
            return (dispatch: Dispatch) => dispatch(submitInvalidQuestionnaire(questionnaireKey));
        }
        return async (dispatch: ThunkDispatch<ApplicationState, undefined, Action>) => {
            const result = await loadAndTrack(dispatch, SUBMIT_QUESTIONNAIRE, clinicalNotesApi.submitReferralClinicalNotes(ohCaseId, taskId));
            toast.success("Clinical Notes submitted successfully");
            dispatch(submitQuestionnaireSuccess(questionnaireKey));
            dispatch(submitClinicalNotesSuccess({
                ohCaseId,
                tasks: result
            }));
            dispatch(navigateToParentRoute());
        };
    };
}

export const savePhysiotherapyReferralClinicalNotes = buildSavePhysiotherapyReferralClinicalNotes(new ClinicalNotesApi(axios));

export const submitPhysiotherapyReferralClinicalNotes = buildSubmitPhysiotherapyReferralClinicalNotes(new ClinicalNotesApi(axios));

export const buildSavePpqClinicalNotes = (clinicalNotesApi: ClinicalNotesApi) => {
    return (ohCaseId: string, taskId: string, customerId: string, questions: AnsweredQuestionWithId[], eTag: string) => {
        return async (dispatch: ThunkDispatch<ApplicationState, undefined, Action>) => {
            const response = await loadAndTrack(dispatch, SAVE_PPQ_CLINICAL_NOTES, clinicalNotesApi.savePpqClinicalNotes(ohCaseId, taskId, questions, eTag));
            dispatch(saveQuestionnaireSuccess({ questionnaireKey: ClinicalNotesQuestionnaireKey, eTag: response.eTag }));
        };
    };
};

export const savePpqClinicalNotes = buildSavePpqClinicalNotes(new ClinicalNotesApi(axios));

export const submitPpqClinicalNotes = buildSubmitPpqClinicalNotes(new ClinicalNotesApi(axios));

export function buildSubmitPpqClinicalNotes(clinicalNotesApi: ClinicalNotesApi) {
    return (ohCaseId: string, taskId: string, customerId: string, questions: AnsweredQuestionWithId[], questionnaireKey: string, tasks: AddTaskFormModel[]) => {
        const temporarilyUnfit = questions.find(q => q.id === outcomeQuestionId)?.answer === PpqOutcome.temporarilyUnfit;
        if (!questions.every(question => question.isValid) || (temporarilyUnfit && tasks.length === 0)) {
            return (dispatch: Dispatch) => dispatch(submitInvalidQuestionnaire(questionnaireKey));
        }
        return async (dispatch: ThunkDispatch<ApplicationState, undefined, Action>) => {
            const result = await loadAndTrack(dispatch, SUBMIT_QUESTIONNAIRE, clinicalNotesApi.submitPpqClinicalNotes(ohCaseId, taskId, questions, tasks));
            toast.success("Clinical Notes submitted successfully");
            dispatch(submitQuestionnaireSuccess(questionnaireKey));
            dispatch(submitClinicalNotesSuccess({
                ohCaseId,
                tasks: result
            }));
            dispatch(navigateToParentRoute());
        };
    };
}

export const submitPpqClinicalNotesAmendments = buildSubmitPpqClinicalNotesAmendments(new ClinicalNotesApi(axios));

export function buildSubmitPpqClinicalNotesAmendments(clinicalNotesApi: ClinicalNotesApi) {
    return (
        ohCaseId: string,
        taskId: string,
        questions: AnsweredQuestionWithId[],
        questionnaireKey: string,
        tasks: AddTaskFormModel[],
        reasonForEdit: string) =>
    {
        const temporarilyUnfit = questions.find(q => q.id === outcomeQuestionId)?.answer === PpqOutcome.temporarilyUnfit;
        if (!questions.every(question => question.isValid) || (temporarilyUnfit && tasks.length === 0)) {
            return (dispatch: Dispatch) => dispatch(submitInvalidQuestionnaire(questionnaireKey));
        }
        return async (dispatch: ThunkDispatch<ApplicationState, undefined, Action>) => {
            const result = await loadAndTrack(
                dispatch,
                SUBMIT_QUESTIONNAIRE,
                clinicalNotesApi.submitPpqClinicalNotesAmendments(ohCaseId, taskId, questions, tasks, reasonForEdit));
            toast.success("Clinical Notes submitted successfully");
            dispatch(submitQuestionnaireSuccess(questionnaireKey));
            dispatch(submitClinicalNotesSuccess({
                ohCaseId,
                tasks: result
            }));
            dispatch(navigateToParentRoute());
        };
    };
}

export const submitDidNotAttend = buildSubmitDidNotAttend(new ClinicalNotesApi(axios));

export function buildSubmitDidNotAttend(clinicalNotesApi: ClinicalNotesApi) {
    return (ohCaseId: string, taskId: string, comment: string) => {
        return async (dispatch: Dispatch) => {
            const result = await loadAndTrack(dispatch, SUBMIT_DID_NOT_ATTEND, clinicalNotesApi.didNotAttend(ohCaseId, taskId, comment));
            dispatch(submitDidNotAttendSuccess({
                ohCaseId,
                tasks: result
            }));
        };
    };
}

export function submitDidNotAttendMultipleAppointments(appointmentTasks: TaskModel[], comment: string) {
    return async (dispatch: Dispatch) => {

        const result = await loadAndTrack(
            dispatch,
            SUBMIT_DID_NOT_ATTEND,
            new ClinicalNotesApi(axios).didNotAttendMultiple(appointmentTasks, comment));

        result.map(r => dispatch(submitDidNotAttendSuccess({ ohCaseId: r.ohCaseId, tasks: r.appointmentTasks })));
    };
}

export const loadConsultationDetails = buildLoadConsultationDetails(new ClinicalNotesApi(axios));

export function buildLoadConsultationDetails(clinicalNotesApi: ClinicalNotesApi) {
    return ({ ohCaseId, taskId }: { ohCaseId: string; taskId: string }) => {
        return async (dispatch: Dispatch) => {
            const consultationDetails = await loadAndTrack(dispatch, "loadConsultation", clinicalNotesApi.loadConsultation(ohCaseId, taskId));
            dispatch(consultationLoaded(consultationDetails));
        };
    };
}

export const buildSubmitBbvClinicalNotes = (clinicalNotesApi: ClinicalNotesApi) => {
    return (ohCaseId: string, taskId: string, customerId: string, questions: AnsweredQuestionWithId[], questionnaireKey: string, task: AddTaskFormModel) => {
        return async (dispatch: Dispatch) => {
            const result = await loadAndTrack(
                dispatch, SUBMIT_QUESTIONNAIRE, clinicalNotesApi.submitBbvClinicalNotes(ohCaseId, taskId, questions, task));
            toast.success("Notes submitted successfully");
            dispatch(submitQuestionnaireSuccess(questionnaireKey));
            dispatch(submitClinicalNotesSuccess({
                ohCaseId,
                tasks: result
            }));
        };
    };
};

export const submitBbvClinicalNotes = buildSubmitBbvClinicalNotes(new ClinicalNotesApi(axios));

export function submitClinicalNotesSuccess(payload: TasksUpdatedSuccessResult): SubmitClinicalNotesSuccess {
    return {
        type: actions.SUBMIT_CLINICAL_NOTES_SUCCESS,
        payload
    };
}

export function submitDidNotAttendSuccess(payload: TasksUpdatedSuccessResult): SubmitDidNotAttendSuccess {
    return {
        type: actions.SUBMIT_DID_NOT_ATTEND_SUCCESS,
        payload
    };
}
